import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { ShoppingBag, ExternalLink, Loader2 } from "lucide-react";
import { useShopProducts } from "@/hooks/useShop";
import type { ShopProduct } from "@/types/api";

function fmtPts(n: number) {
  return n.toLocaleString("pt-BR");
}

interface ProductVitrineModalProps {
  handle: string;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function ProductCard({ product }: { product: ShopProduct }) {
  const imageUrl = product.images[0]?.image_url;

  return (
    <div className="flex flex-col gap-4 p-1 md:flex-row">
      <div className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-lg bg-muted md:w-80">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={product.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <ShoppingBag className="h-16 w-16 text-muted-foreground/40" />
        )}
      </div>

      <div className="flex flex-1 flex-col justify-between gap-4">
        <div className="space-y-3">
          <div>
            <h3 className="text-xl font-semibold">{product.name}</h3>
            {product.description && (
              <p className="mt-1 text-sm text-muted-foreground">
                {product.description}
              </p>
            )}
          </div>

          {product.description_long && (
            <p className="whitespace-pre-line text-sm text-muted-foreground">
              {product.description_long}
            </p>
          )}

          <div className="inline-block rounded-full bg-primary/10 px-4 py-2 text-lg font-bold text-primary">
            {fmtPts(product.price_points)} pts
          </div>
        </div>

        <Button asChild className="gap-2">
          <a
            href={`/resgate/loja/${product.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink className="h-4 w-4" />
            Resgatar Produto
          </a>
        </Button>
      </div>
    </div>
  );
}

export function ProductVitrineModal({
  handle,
  trigger,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: ProductVitrineModalProps) {
  const { data: products = [], isLoading } = useShopProducts(handle);
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled
    ? (v: boolean) => controlledOnOpenChange?.(v)
    : setInternalOpen;

  useEffect(() => {
    if (!open) return;
  }, [open]);

  const activeProducts = products.filter((p) => p.active);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogTitle className="sr-only">Vitrine de Produtos</DialogTitle>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : activeProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-muted-foreground">
            <ShoppingBag className="h-12 w-12" />
            <p>Nenhum produto disponível no momento.</p>
          </div>
        ) : (
          <Carousel className="w-full">
            <CarouselContent>
              {activeProducts.map((product) => (
                <CarouselItem key={product.id}>
                  <ProductCard product={product} />
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="mt-4 flex items-center justify-center gap-2">
              <CarouselPrevious />
              <span className="text-sm text-muted-foreground">
                {activeProducts.length}{" "}
                {activeProducts.length === 1 ? "produto" : "produtos"}
              </span>
              <CarouselNext />
            </div>
          </Carousel>
        )}
      </DialogContent>
    </Dialog>
  );
}
